/**
 * @fileoverview Completion step for onboarding - success message and next actions.
 * @module features/onboarding/components/steps/CompletionStep
 */

import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard } from 'lucide-react'
import { ScrollReveal } from '@/shared/components/ui'

interface CompletionStepProps {
  /** Organization name to display */
  orgName?: string
}

/**
 * Final step of onboarding - shows success and next actions.
 *
 * @component
 * @example
 * ```tsx
 * <CompletionStep orgName="Acme Inc." />
 * ```
 */
export function CompletionStep({ orgName }: CompletionStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')
  const navigate = useNavigate()

  return (
    <div className="flex flex-col items-center justify-center max-w-lg mx-auto text-center">
      {/* Logo */}
      <ScrollReveal animateOnMount blur delay={0}>
        <img
          src="/ab-black-white-pill-logo.webp"
          alt="AbyssBlock"
          className="h-24 w-auto mb-6"
        />
      </ScrollReveal>

      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0.1}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('completion.title', "You're all set!")}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.15}>
        <p className="text-muted-foreground mb-8 text-lg">
          {orgName
            ? t('completion.subtitleWithName', '{{name}} is ready to use', { name: orgName })
            : t('completion.subtitle', 'Your workspace is ready to use')}
        </p>
      </ScrollReveal>

      {/* Next action */}
      <ScrollReveal animateOnMount blur delay={0.2}>
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-3 px-6 py-3 bg-card rounded-xl border border-border/15 hover:bg-muted/50 transition-colors"
        >
          <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">
            {t('completion.actions.dashboard', 'Dashboard')}
          </span>
        </button>
      </ScrollReveal>

    </div>
  )
}
